import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import styled from 'styled-components';
import { commifyNumber, getImagePath, getReadyBy } from '../../helpers/common';
import { CardClickEventData } from '../../new-lib/types/common';
import CardTags from './CardTags';
import { handleCardClickEvent } from '../../helpers/cards';
import { EntityTypes } from '../../new-lib/constants/common';
import ImageWrapper from '../ImageWrapper';
import CardCompareBtn from './components/CardCompareBtn';
import CardCTA from './components/CardCTA';
import { useRouter } from 'next/router';
import ImageAltDescription from '../../helpers/images-alt';

interface CardProps {
  property: any;
  clickEventData: CardClickEventData;
  cardPosition?: number;
}
const CardUIListType2 = ({ property, clickEventData }: CardProps) => {
  const { t } = useTranslation(['common', 'entity']);
  const { asPath, locale } = useRouter();
  const getDeliveryDate = () => {
    return getReadyBy(property.min_ready_by);
  };

  const imageAltDescription = new ImageAltDescription(
    property,
    'property',
    locale
  );
  const altDescription = imageAltDescription.getAltDescription();

  return (
    <Link
      href={`/compound/[compound_slug]/property/[property_slug]`}
      as={`/compound/${property.compound?.slug}/property/${
        property?.slug || property?.id
      }`}
      prefetch={false}
    >
      <Container onClick={() => handleCardClickEvent(clickEventData)}>
        <div className="upper-details">
          <CardCompareBtn
            clickEventData={clickEventData}
            entityData={{ id: property.id, min_price: property.min_price }}
            entityType={EntityTypes.PROPERTY}
          />
          {(property.is_launch_summary ||
            (property.tags && property.tags.length > 0)) && (
            <CardTags
              tags={[
                ...(property.tags as Array<string>),
                ...(property.is_launch_summary ? ['Launch'] : [])
              ]}
              isCustom={true}
            />
          )}
        </div>
        <div className="left-container">
          <ImageWrapper
            objectFit="cover"
            src={getImagePath(property.image)}
            alt={altDescription}
            fill
            withSkeleton
            imageSize="medium"
            quality={50}
            sizes="50vw"
          />
        </div>
        <div className="right-container">
          <h3 className="compound-name">{property.property_type?.name}</h3>
          {asPath.includes('/nawy-now') ? (
            <h4 className="compound-name">{property.compound?.name}</h4>
          ) : getDeliveryDate() == 'delivered' ? (
            <span className="delivery-status">{t('delivered')}</span>
          ) : property.min_ready_by ? (
            <span className="delivery-status">{`${t(
              'delivery_in'
            )} ${getDeliveryDate()}`}</span>
          ) : null}
          <div className="property-details">
            {property.number_of_bedrooms ? (
              <span>{`${property.number_of_bedrooms} ${t('beds')}`}</span>
            ) : null}
            {property.number_of_bathrooms ? (
              <span>{`${property.number_of_bathrooms} ${t('baths')}`}</span>
            ) : null}
            {property.min_unit_area ? (
              <span>{`${property.min_unit_area} ${t('m²')}`}</span>
            ) : null}
            {!property.number_of_bedrooms &&
              !property.number_of_bathrooms &&
              !property.min_unit_area && (
                <h4>{t('details_tba', { ns: 'entity' })}</h4>
              )}
          </div>
          <div className="price-cta">
            {property.min_price ? (
              <h4 className="property-price">
                {`${commifyNumber(property.min_price)} ${
                  property.currency || 'EGP'
                }`}
              </h4>
            ) : (
              <span className="price">{t('price_tba', { ns: 'entity' })}</span>
            )}
            <CardCTA
              clickEventData={clickEventData}
              entity={property}
              entityType={EntityTypes.PROPERTY}
            />
          </div>
        </div>
      </Container>
    </Link>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 125px;
  display: flex;
  background: #ffffff;
  border: 1px solid #e6e6e6;
  border-radius: ${props => props.theme.borderRadius};

  .upper-details {
    width: 35%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    z-index: 9;
    position: absolute;
    top: 6px;
    padding: 0 6px;
  }
  .left-container {
    position: relative;
    width: 35%;
    height: 100%;

    img {
      border-start-start-radius: 8px;
      border-start-end-radius: 0px;
      border-end-start-radius: 8px;
      border-end-end-radius: 0px;
    }
  }

  .right-container {
    width: 65%;
    height: 100%;
    padding: 8px;
    display: flex;
    flex-direction: column;
    h3 {
      font-size: ${props => props.theme.font.sizes.s};
      color: ${props => props.theme.light.colors.black};
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      &.compound-name {
        font-family: materialMedium;
        margin-bottom: 5px;
      }
    }

    h4 {
      font-family: materialRegular;
      font-size: ${props => props.theme.font.sizes.xs};
      color: ${props => props.theme.light.colors.black};
      margin-bottom: 5px;

      &.property-price {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-size: ${props => props.theme.font.sizes.m};
        font-family: materialBold;
        margin-bottom: 0px;
      }
    }

    .delivery-status {
      font-family: materialRegular;
      font-size: ${props => props.theme.font.sizes.xs};
      color: ${props => props.theme.light.colors.black};
      margin-bottom: 5px;
    }

    .property-details {
      display: flex;
      align-items: center;
      font-family: materialRegular;
      font-size: ${props => props.theme.font.sizes.xs};
      color: ${props => props.theme.light.colors.black};
      margin-bottom: 5px;

      span {
        padding-inline-end: 5px;
      }

      span:not(:first-child) {
        padding-inline-start: 5px;
        border-inline-start: 1px solid #c4c4c4;
      }
    }

    .price-cta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: auto;
      .price {
        font-size: 13px;
      }
    }
  }
`;

export default CardUIListType2;
